const ROUTER_ENABLED =
    String(import.meta.env.VITE_STATIC_API_ROUTER || "").toLowerCase() === "true";

const SERVICE_ORIGINS = {
    auth:
        import.meta.env.VITE_AUTH_SERVICE_PUBLIC_URL ||
        "https://auth-service-ak0f.onrender.com",
    playlist:
        import.meta.env.VITE_PLAYLIST_SERVICE_PUBLIC_URL ||
        "https://playlist-service-jwbm.onrender.com",
    vote:
        import.meta.env.VITE_VOTE_SERVICE_PUBLIC_URL ||
        "https://vote-service-7p4o.onrender.com",
    payment:
        import.meta.env.VITE_PAYMENT_SERVICE_PUBLIC_URL ||
        "https://payment-service-ijlo.onrender.com",
    music:
        import.meta.env.VITE_MUSIC_SERVICE_PUBLIC_URL ||
        "https://music-service-b3dm.onrender.com",
    socket:
        import.meta.env.VITE_SESSION_SERVICE_PUBLIC_URL ||
        "https://socket-service-y42a.onrender.com",
};

const withOrigin = (origin: string, path: string, search = "") =>
    `${origin}${path}${search}`;

export const isStaticApiRouterEnabled = () => ROUTER_ENABLED;

export const toStaticServiceUrl = (
    service: keyof typeof SERVICE_ORIGINS,
    path: string
): string => {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    if (!ROUTER_ENABLED) return normalizedPath;

    let directPath = normalizedPath;

    if (service === "auth" && directPath.startsWith("/api/auth/")) {
        directPath = directPath.replace(/^\/api\/auth/, "/auth");
    }

    return `${SERVICE_ORIGINS[service]}${directPath}`;
};

export const rewriteStaticApiUrl = (value: string | URL): string | URL => {
    if (!ROUTER_ENABLED || typeof window === "undefined") return value;

    const raw = value instanceof URL ? value.toString() : String(value);
    let url: URL;

    try {
        url = new URL(raw, window.location.origin);
    } catch {
        return value;
    }

    if (url.origin !== window.location.origin) return value;

    const { pathname, search } = url;

    if (pathname.startsWith("/uploads/")) {
        return withOrigin(SERVICE_ORIGINS.playlist, pathname, search);
    }

    if (pathname.startsWith("/socket.io/") || pathname.startsWith("/ws")) {
        return withOrigin(SERVICE_ORIGINS.socket, pathname, search);
    }

    if (pathname.startsWith("/api/auth/v1/")) {
        return withOrigin(
            SERVICE_ORIGINS.auth,
            pathname.replace(/^\/api\/auth\/v1/, "/auth/v1"),
            search
        );
    }

    if (pathname.startsWith("/api/auth/") || pathname.startsWith("/auth/")) {
        return withOrigin(SERVICE_ORIGINS.auth, pathname.replace(/^\/api\/auth/, ""), search);
    }

    if (pathname === "/api/v1/auth/refresh") {
        return withOrigin(SERVICE_ORIGINS.auth, "/auth/v1/refresh", search);
    }

    if (
        pathname.startsWith("/api/v1/music-auth/") ||
        /^\/api\/v1\/auth\/(youtube|spotify|soundcloud|apple)\/(user-)?callback$/.test(pathname)
    ) {
        const musicPath = pathname
            .replace(/^\/api\/v1\/music-auth\//, "/api/v1/auth/")
            .replace(
                /^\/api\/v1\/auth\/(youtube|spotify|soundcloud|apple)\/callback$/,
                "/api/v1/auth/$1/user-callback"
            );
        return withOrigin(SERVICE_ORIGINS.music, musicPath, search);
    }

    if (pathname.startsWith("/api/v1/search/")) {
        return withOrigin(SERVICE_ORIGINS.music, pathname, search);
    }

    if (
        pathname.startsWith("/api/v1/sessions") ||
        pathname.startsWith("/api/session")
    ) {
        return withOrigin(SERVICE_ORIGINS.socket, pathname, search);
    }

    if (
        pathname.startsWith("/api/v1/playlists") ||
        pathname.startsWith("/api/v1/songs") ||
        pathname.startsWith("/api/v1/venues") ||
        pathname.startsWith("/api/v1/suggestions")
    ) {
        return withOrigin(SERVICE_ORIGINS.playlist, pathname, search);
    }

    if (pathname.startsWith("/api/v1/votes")) {
        return withOrigin(SERVICE_ORIGINS.vote, pathname, search);
    }

    if (
        pathname.startsWith("/api/v1/payments") ||
        pathname.startsWith("/api/v1/plans") ||
        pathname.startsWith("/api/v1/webhooks")
    ) {
        return withOrigin(SERVICE_ORIGINS.payment, pathname, search);
    }

    return value;
};

export const installStaticApiRouter = () => {
    if (!ROUTER_ENABLED || typeof window === "undefined") return;

    const originalFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
        if (input instanceof Request) {
            return originalFetch(new Request(rewriteStaticApiUrl(input.url).toString(), input), init);
        }

        return originalFetch(rewriteStaticApiUrl(input) as RequestInfo | URL, init);
    };

    const originalOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (
        method: string,
        url: string | URL,
        async = true,
        username?: string | null,
        password?: string | null
    ) {
        return originalOpen.call(
            this,
            method,
            rewriteStaticApiUrl(url).toString(),
            async,
            username,
            password
        );
    };

    const OriginalWebSocket = window.WebSocket;
    window.WebSocket = function (
        url: string | URL,
        protocols?: string | string[]
    ) {
        return new OriginalWebSocket(rewriteStaticApiUrl(url).toString(), protocols);
    } as typeof WebSocket;
};
